
import React, { useMemo, useEffect, useCallback, useState } from "react";
import { Box , useToast,} from "@chakra-ui/react";
import AgGridWrapper from "components/AgGrid/AgGridWrapper";
import apiClient from "Configuration/apiClient";
import GridPagination from "components/CustomPagination/GridPagination";
import EditImageModal from "views/SoftDrinks/EditImageModal";
import API_CONFIG from "Configuration/apiConfig";

const RecalculateButton = ({ rowData, fromDate, toDate }) => {
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  const handleClick = async () => {
    const { runId, storeId } = rowData;

    if (!runId || !storeId) {
      toast({
        title: "Missing Data",
        description: "Missing runId or storeId for this row.",
        status: "error",
        duration: 3000,
        position: "top",
        isClosable: true,
      });
      return;
    }

    try {
      setIsLoading(true);

      const query = new URLSearchParams({ runId, storeId }).toString();

      const response = await fetch(
        `https://orgi-api.sconexsoft.com/aicalc/calculate-score?${query}`,
        { method: "GET" }
      );

      if (!response.ok) throw new Error("API failed");
      await response.text();

      toast({
        title: "Success",
        description: "Store recalculated successfully",
        status: "success",
        duration: 3000,
        position: "top",
        isClosable: true,
      });

    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Recalculation failed",
        status: "error",
        duration: 3000,
        position: "top",
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={isLoading}
      style={{
        backgroundColor: isLoading ? "#905656" : "#C53030",
        color: "white",
        border: "none",
        borderRadius: "6px",
        padding: "4px 10px",
        fontSize: "13px",
        fontWeight: 600,
        cursor: isLoading ? "not-allowed" : "pointer",
        opacity: isLoading ? 0.7 : 1,
        minWidth: "90px",
      }}
      onMouseEnter={(e) => { if (!isLoading) e.target.style.backgroundColor = "#9B2C2C"; }}
      onMouseLeave={(e) => { if (!isLoading) e.target.style.backgroundColor = "#C53030"; }}
    >
      {isLoading ? "Loading..." : "Recalculation"}
    </button>
  );
};

const col = (headerName, field, filter = "agTextColumnFilter", extra = {}) => ({
  headerName, field, filter, ...extra,
});

const NUMBER = "agNumberColumnFilter";
const RIGHT  = { cellStyle: { textAlign: "right" } };

const parseDate = (dateStr) => {
  const [year, month, day] = dateStr.split("-").map(Number);
  return { year, month, date: day };
};


const mapRow = (item) => ({
  outletCode:   item.storeid        ?? "",
  storeName:    item.storeName      ?? "",
  storeType:    item.storeType      ?? "",
  ProductBrand: item.productName    ?? "",
  Count:        item.totalProductCount ?? 0,
  runId:        item.runid          ?? null,
  storeId:      item.storeid        ?? null,
  fileName:     item.filename       ?? null,
});

export default function UnResolvedProductsDp({ fromDate, toDate }) {

  const [rowData,       setRowData]       = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState(null);
  const [currentPage,   setCurrentPage]   = useState(0);
  const [totalPages,    setTotalPages]    = useState(1);
  const [totalElements, setTotalElements] = useState(0);

  // ── Modal state ────────────────────────────────────────────────
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const handleEditClick = (data) => {
    setSelectedRow(data);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedRow(null);
  };

  const handleModalSave = async () => {
    await fetchData();
  };

  // ── Column Defs ────────────────────────────────────────────────
  const COLUMN_DEFS = useMemo(() => [
    col("Outlet Code",   "outletCode",   undefined, { minWidth: 120 ,width:100}),
    col("Store Name",    "storeName",    undefined, { minWidth: 160, width: 200 }),
    col("Store Type",    "storeType",    undefined, { minWidth: 140, width: 150 }),
    col("Product Name",  "ProductBrand", undefined, { minWidth: 170, width: 150 }),
    col("Count",         "Count",        NUMBER,    { minWidth: 130, width: 120, ...RIGHT }),
    {
      headerName: "Action",
      field: "action",
      filter: false,
      sortable: false,
      minWidth: 150,
      width: 120,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
      cellRenderer: (params) => (
        <button
          onClick={() => handleEditClick(params.data)}
          style={{
            backgroundColor: "#C53030", color: "white", border: "none",
            borderRadius: "6px", padding: "4px 14px", fontSize: "13px",
            fontWeight: 600, cursor: "pointer",
          }}
          onMouseEnter={(e) => (e.target.style.backgroundColor = "#9B2C2C")}
          onMouseLeave={(e) => (e.target.style.backgroundColor = "#C53030")}
        >
          Edit
        </button>
      ),
    },
    {
    headerName: "Recalculation",
    field: "recalculation",
    filter: false,
    sortable: false,
    minWidth: 140,
    width: 130,
    cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
    cellRenderer: (params) => (
      <RecalculateButton
        rowData={params.data}
        fromDate={fromDate}
        toDate={toDate}
      />
    ),
  },
], [fromDate, toDate]);
 

  useEffect(() => {
    setCurrentPage(0);
  }, [fromDate, toDate]);

  const fetchData = useCallback(async () => {
    if (!fromDate || !toDate) return;

    setLoading(true);
    setError(null);
    setRowData([]);

    try {
      const response = await apiClient.post(
        `${API_CONFIG.ENDPOINTS.UnResolved_products}?page=${currentPage}&size=10`,
        {
          from: parseDate(fromDate),
          to:   parseDate(toDate),
          
        }
      );
      const result = response.data;
      setRowData((result.content ?? []).map(mapRow));
      setTotalPages(result.totalPages ?? 1);
      setTotalElements(result.totalElements ?? 0);
    } catch (err) {
      console.error("UnResolvedProductsDp fetch error:", err);
      setError("Failed to load data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, currentPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePrevious = () => { if (currentPage > 0) setCurrentPage((p) => p - 1); };
  const handleNext     = () => { if (currentPage < totalPages - 1) setCurrentPage((p) => p + 1); };

  return (
    <Box px={3} pb={8}>
      <AgGridWrapper
        rowData={rowData}
        columnDefs={COLUMN_DEFS}
        loading={loading}
        noRowsMessage={error ?? "No unresolved products found for the selected date range."}
        extraGridProps={{ headerHeight: 60 }}
      />
      <GridPagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalElements={totalElements}
        pageSize={10}
        onPrevious={handlePrevious}
        onNext={handleNext}
      />

      {/* ── Edit Modal ── */}
      {selectedRow && (
        <EditImageModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          runId={selectedRow.runId}        
          storeId={selectedRow.storeId}    
          fileName={selectedRow.fileName}  
          preloadedImageUrl={null}
          onSave={handleModalSave}
          
        />
      )}
    </Box>
  );
}